import React from 'react';

import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import type { HeroCarouselSlideProps } from 'src/components/organisms/HeroCarousel/HeroCarousel';
import { HeroCarousel } from 'src/components/organisms/HeroCarousel/HeroCarousel';
import {
  trackHeroCarouselClick,
  trackHeroCarouselImpression,
} from 'src/tracking/common/trackHeroCarousel';
import * as styles from './HeroCarouselSection.style';

export interface HeroCarouselSectionProps {
  isFirstSection?: boolean;
  items: HeroCarouselSlideProps[];
  // Supplied by the content pipeline but unused here: content_position is always "1" for the hero.
  trackingPosition?: number;
  trackingTitle?: string;
}

// The tracking <name> for each slide is its heading lines joined together, e.g. "f day experience".
const getItemName = ({ headingLine1, headingLine2 }: HeroCarouselSlideProps) =>
  [headingLine1, headingLine2].filter(Boolean).join(' ');

export const HeroCarouselSection = ({ isFirstSection, items }: HeroCarouselSectionProps) => {
  const trackImpressionForSlide = (index: number) => {
    const item = items[index];

    if (item) {
      trackHeroCarouselImpression({
        name: getItemName(item),
        totalOptions: items.length,
        trackingContentType: 'Hero carousel',
      });
    }
  };

  return (
    <div css={styles.container}>
      <SectionContainer
        wrapperStyles={isFirstSection ? styles.firstSectionWrapper : styles.wrapper}
      >
        <HeroCarousel
          autoplay={true}
          items={items}
          onItemButtonClick={(e, index) =>
            trackHeroCarouselClick(e, {
              clickType: 'cta click',
              name: getItemName(items[index]),
              selectedPosition: index + 1,
              totalOptions: items.length,
              trackingContentType: 'Hero carousel',
            })
          }
          onItemImageClick={(e, index) =>
            trackHeroCarouselClick(e, {
              clickType: 'image click',
              name: getItemName(items[index]),
              selectedPosition: index + 1,
              totalOptions: items.length,
              trackingContentType: 'Hero carousel',
            })
          }
          onItemTermsClick={(e, index) =>
            trackHeroCarouselClick(e, {
              clickType: 'terms click',
              name: getItemName(items[index]),
              selectedPosition: index + 1,
              totalOptions: items.length,
              trackingContentType: 'Hero carousel',
            })
          }
          // Fire an impression for each slide as it becomes the active one...
          onSlideChange={(index) => trackImpressionForSlide(index)}
          // ...and for the first slide when the carousel initially enters the viewport.
          onVisible={() => trackImpressionForSlide(0)}
        />
      </SectionContainer>
    </div>
  );
};
